"use client";

import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { flattenDictionary, translateKey, type dictionary, type Params } from "@dictionaries/i18";

/**
 * Hook for using translations in client components
 * @param fileName - Optional name of the translation file (e.g., "footer"). If not provided, uses the main dictionary (en.json, es.json)
 * @returns Object with t function, language, and isLoading state
 *
 * @example
 * // File-based translation (new way)
 * const { t, language, isLoading } = useTranslation("footer");
 * const contactText = t("Contact", { name: "John" });
 *
 * @example
 * // Main dictionary (old way, compatible with context.t)
 * const { t, language } = useTranslation();
 * const text = t("someKey");
 */
export const useTranslation = (fileName?: string) => {
  const { context } = useContext(GlobalContext);
  const language = context.language || "en-US";
  const [dictionary, setDictionary] = useState<dictionary>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadTranslations = async () => {
      try {
        setIsLoading(true);
        let rawDictionary;

        if (fileName) {
          // Load from file structure: dictionaries/{language}/{fileName}.json
          rawDictionary = await import(`../../dictionaries/${language}/${fileName}.json`).then(
            (module) => module.default
          );
        } else {
          // Load from main dictionaries: dictionaries/{language}.json (en-US.json, es.json)
          rawDictionary = await import(`../../dictionaries/${language}.json`).then(
            (module) => module.default
          );
        }

        if (isMounted) {
          // Flatten the dictionary to support nested objects
          const flattened = flattenDictionary(rawDictionary);
          setDictionary(flattened);
          setIsLoading(false);
        }
      } catch (error) {
        console.warn(
          fileName
            ? `Translation file "${fileName}" for language "${language}" not found. Falling back to empty dictionary.`
            : `Main translation file for language "${language}" not found. Falling back to empty dictionary.`
        );
        if (isMounted) {
          setDictionary({});
          setIsLoading(false);
        }
      }
    };

    loadTranslations();

    return () => {
      isMounted = false;
    };
  }, [fileName, language]);

  const t = (key: string, params?: Params): string => {
    return translateKey(key, dictionary, params);
  };

  return { t, language, isLoading };
};
